import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, Pencil } from "lucide-react";

import { Button } from "../../../components/ui/button";
import { Skeleton } from "../../../components/feedback/Skeleton";
import { EmptyState } from "../../../components/feedback/EmptyState";
import { ErrorState } from "../../../components/feedback/ErrorState";
import { adminUsersService } from "../../../services/adminUsers";
import { ordersService } from "../../../services/orders";
import { formatApiError } from "../../../services/api";
import { useAuth } from "../../../app/providers/AuthProvider";
import { UserFormModal } from "../components/UserFormModal";
import { ResetPasswordModal } from "../components/ResetPasswordModal";

const ROLE_BADGE = {
  standard: "bg-offwhite text-text-secondary border border-border-subtle",
  warehouse: "bg-status-progress/15 text-status-progress border border-status-progress/30",
  admin: "bg-champagne/25 text-charcoal border border-champagne/50",
};

const STATUS_TONE = {
  draft: "bg-offwhite text-text-secondary border-border-subtle",
  cancelled: "bg-status-unavailable/10 text-status-unavailable border-status-unavailable/30",
  collection_complete: "bg-status-available/10 text-status-available border-status-available/30",
};

function fmtDate(iso, lang) {
  if (!iso) return "—";
  const locale = lang?.startsWith("en") ? "en-GB" : "de-DE";
  return new Date(iso).toLocaleDateString(locale, { year: "numeric", month: "short", day: "2-digit" });
}

function InfoRow({ label, value, testId, mono }) {
  return (
    <div className="grid grid-cols-3 gap-2 py-2 text-sm">
      <div className="text-[11px] uppercase tracking-wide text-text-secondary">{label}</div>
      <div
        data-testid={testId}
        className={`col-span-2 text-text-primary ${mono ? "font-mono text-xs" : ""}`}
      >
        {value || "—"}
      </div>
    </div>
  );
}

export default function UserDetailPage() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);

  const [editOpen, setEditOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  const loadUser = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const u = await adminUsersService.get(id);
      setUser(u);
    } catch (err) {
      setError(formatApiError(err, t("common.error_generic")));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  const loadOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const d = await ordersService.listAll({
        user_ids: id,
        include_drafts: true,
        sort: "created_at:desc",
        page: 1,
        page_size: 10,
      });
      setOrders(d.items || []);
    } catch {
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadUser();
    loadOrders();
  }, [loadUser, loadOrders]);

  const isSelf = user && currentUser?.id === user.id;
  const languageLabel = user?.language === "en" ? "English (EN)" : "Deutsch (DE)";

  return (
    <>
      <div className="space-y-6" data-testid="user-detail-page">
        <div className="flex flex-col gap-3">
          <Link
            to="/users"
            data-testid="user-detail-back"
            className="inline-flex items-center gap-1 text-xs text-text-secondary hover:text-charcoal"
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
            {t("admin.users.back_to_list")}
          </Link>

          {loading ? (
            <Skeleton className="h-9 w-64" />
          ) : error ? (
            <ErrorState
              title={t("common.error_generic")}
              body={error}
              onRetry={loadUser}
              testId="user-detail-error"
            />
          ) : user ? (
            <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
              <div className="min-w-0">
                <h1
                  className="text-3xl font-semibold tracking-tight text-text-primary"
                  data-testid="user-detail-name"
                >
                  {user.name}
                  {isSelf && (
                    <span className="ml-3 rounded bg-champagne/30 px-1.5 py-0.5 align-middle text-[11px] font-semibold text-charcoal">
                      {t("admin.users.you")}
                    </span>
                  )}
                </h1>
                <p className="mt-1 text-sm text-text-secondary" data-testid="user-detail-email">
                  {user.email}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={() => setResetOpen(true)}
                  data-testid="user-detail-reset-password"
                  className="border-border-subtle text-text-primary hover:bg-offwhite"
                >
                  <KeyRound className="mr-2 h-4 w-4" />
                  {t("admin.users.reset_password_tooltip")}
                </Button>
                <Button
                  onClick={() => setEditOpen(true)}
                  data-testid="user-detail-edit"
                  className="bg-charcoal text-white hover:bg-charcoal-soft focus-visible:ring-champagne"
                >
                  <Pencil className="mr-2 h-4 w-4" />
                  {t("admin.users.edit_title")}
                </Button>
              </div>
            </div>
          ) : null}
        </div>

        {!loading && !error && user && (
          <div className="grid gap-6 lg:grid-cols-2">
            <section
              className="rounded-lg border border-border-subtle bg-surface p-6 shadow-card"
              data-testid="user-detail-account"
            >
              <div className="mb-3 flex items-baseline justify-between">
                <h2 className="text-lg font-semibold text-text-primary">
                  {t("admin.users.detail_account_heading")}
                </h2>
                <div className="h-px w-8 bg-champagne" aria-hidden />
              </div>
              <div className="divide-y divide-border-subtle">
                <InfoRow
                  label={t("admin.users.col_email")}
                  value={user.email}
                  testId="user-detail-info-email"
                  mono
                />
                <InfoRow
                  label={t("admin.users.col_first_name")}
                  value={user.first_name}
                  testId="user-detail-info-first-name"
                />
                <InfoRow
                  label={t("admin.users.col_last_name")}
                  value={user.last_name}
                  testId="user-detail-info-last-name"
                />
                <InfoRow
                  label={t("admin.users.col_phone")}
                  value={user.phone}
                  testId="user-detail-info-phone"
                  mono
                />
                <div className="grid grid-cols-3 gap-2 py-2 text-sm">
                  <div className="text-[11px] uppercase tracking-wide text-text-secondary">
                    {t("admin.users.col_role")}
                  </div>
                  <div className="col-span-2">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${ROLE_BADGE[user.role] || ROLE_BADGE.standard}`}
                      data-testid={`user-detail-role-${user.role}`}
                    >
                      {t(`admin.users.role_${user.role}`)}
                    </span>
                  </div>
                </div>
                <InfoRow
                  label={t("admin.users.field_language")}
                  value={languageLabel}
                  testId="user-detail-info-language"
                />
                <InfoRow
                  label={t("admin.users.detail_created_at")}
                  value={fmtDate(user.created_at, i18n.language)}
                  testId="user-detail-info-created"
                  mono
                />
                <InfoRow
                  label={t("admin.users.detail_updated_at")}
                  value={fmtDate(user.updated_at, i18n.language)}
                  testId="user-detail-info-updated"
                  mono
                />
              </div>
            </section>
          </div>
        )}

        {!loading && !error && user && (
          <section
            className="rounded-lg border border-border-subtle bg-surface p-6 shadow-card"
            data-testid="user-detail-orders"
          >
            <div className="mb-3 flex items-baseline justify-between">
              <h2 className="text-lg font-semibold text-text-primary">
                {t("admin.users.detail_orders_heading")}
              </h2>
              <div className="h-px w-8 bg-champagne" aria-hidden />
            </div>

            {ordersLoading ? (
              <div className="space-y-2">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : orders.length === 0 ? (
              <EmptyState
                title={t("admin.users.detail_no_orders_title")}
                body={t("admin.users.detail_no_orders_body")}
                testId="user-detail-orders-empty"
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-offwhite text-left text-[11px] uppercase tracking-wide text-text-secondary">
                    <tr>
                      <th className="px-4 py-3 font-semibold">{t("orders.columns.order_number")}</th>
                      <th className="px-4 py-3 font-semibold">{t("orders.columns.event")}</th>
                      <th className="px-4 py-3 font-semibold">{t("orders.columns.period")}</th>
                      <th className="px-4 py-3 font-semibold">{t("orders.columns.status")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-subtle">
                    {orders.map((o) => (
                      <tr
                        key={o.id}
                        onClick={() => navigate(`/orders/${o.id}`)}
                        data-testid={`user-detail-order-row-${o.id}`}
                        className="cursor-pointer transition-colors hover:bg-offwhite/50"
                      >
                        <td className="px-4 py-3 font-mono text-xs text-text-primary">
                          {o.order_number || ""}
                        </td>
                        <td className="px-4 py-3 text-text-primary">
                          {o.event_name || "—"}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-text-secondary">
                          {fmtDate(o.from_date, i18n.language)} — {fmtDate(o.until_date, i18n.language)}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${STATUS_TONE[o.status] || "bg-offwhite text-text-secondary border-border-subtle"}`}
                          >
                            {t(`orders.status.${o.status}`)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
      </div>

      <UserFormModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        editingUser={user}
        onSaved={() => {
          setEditOpen(false);
          loadUser();
        }}
      />

      <ResetPasswordModal
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        user={user}
      />
    </>
  );
}

export function _testables() {
  // Named export block for future unit tests — no-op export placeholder.
  return null;
}
